import { functionUrls } from '../../../services/firebaseFunctions';
import { loginMachine } from '../../../auth/loginMachine';
import type { UnderstandingPoint } from '../types/detailsCard.types';

export interface UnderstandingPointUpdate {
  index: number;
  title?: string;
  body?: string;
  bodyDelta?: string;
}

function normalizePoints(payload: any): UnderstandingPoint[] {
  const rawPoints = Array.isArray(payload?.points)
    ? payload.points
    : Array.isArray(payload)
      ? payload
      : [];

  return rawPoints
    .map((item: any) => ({
      title: String(item?.title || '').trim(),
      body: String(item?.body || '').trim(),
    }))
    .filter((item: UnderstandingPoint) => item.title && item.body);
}

function tryParseJson(raw: string): any {
  const direct = raw.trim();
  if (!direct) return null;

  try {
    return JSON.parse(direct);
  } catch {}

  const markdownJsonMatch = direct.match(/```json\s*([\s\S]*?)\s*```/i);
  if (markdownJsonMatch?.[1]) {
    try {
      return JSON.parse(markdownJsonMatch[1]);
    } catch {}
  }

  const objectMatch = direct.match(/\{[\s\S]*\}/);
  if (objectMatch?.[0]) {
    try {
      return JSON.parse(objectMatch[0]);
    } catch {}
  }

  return null;
}

function decodePartialJsonString(value: string): string {
  return value
    .replace(/\\\\/g, '\\')
    .replace(/\\"/g, '"')
    .replace(/\\n/g, '\n')
    .replace(/\\r/g, '\r')
    .replace(/\\t/g, '\t');
}

function readJsonStringField(
  input: string,
  fieldName: string,
): { value: string | null; closed: boolean } {
  const keyMatch = new RegExp(`"${fieldName}"\\s*:\\s*"`).exec(input);
  if (!keyMatch) return { value: null, closed: false };

  const start = (keyMatch.index ?? 0) + keyMatch[0].length;
  let escaped = false;
  let out = '';
  for (let i = start; i < input.length; i += 1) {
    const ch = input[i];
    if (escaped) {
      out += `\\${ch}`;
      escaped = false;
      continue;
    }
    if (ch === '\\') {
      escaped = true;
      continue;
    }
    if (ch === '"') {
      try {
        return { value: JSON.parse(`"${out}"`), closed: true };
      } catch {
        return { value: decodePartialJsonString(out), closed: true };
      }
    }
    out += ch;
  }

  return { value: decodePartialJsonString(out), closed: false };
}

function extractObjectDraftsFromPointsArray(raw: string): Array<{
  index: number;
  title?: string;
  body?: string;
}> {
  const pointsKeyIdx = raw.indexOf('"points"');
  const arrayStart = pointsKeyIdx >= 0
    ? raw.indexOf('[', pointsKeyIdx)
    : raw.trimStart().startsWith('[')
      ? raw.indexOf('[')
      : -1;
  if (arrayStart < 0) return [];

  const drafts: Array<{ index: number; title?: string; body?: string }> = [];
  let inString = false;
  let escaped = false;
  let objectDepth = 0;
  let objectStart = -1;
  let objectIndex = 0;

  for (let i = arrayStart + 1; i < raw.length; i += 1) {
    const ch = raw[i];
    if (inString) {
      if (escaped) {
        escaped = false;
      } else if (ch === '\\') {
        escaped = true;
      } else if (ch === '"') {
        inString = false;
      }
      continue;
    }

    if (ch === '"') {
      inString = true;
      continue;
    }

    if (ch === '{') {
      if (objectDepth === 0) objectStart = i;
      objectDepth += 1;
      continue;
    }

    if (ch === '}') {
      objectDepth = Math.max(0, objectDepth - 1);
      if (objectDepth === 0 && objectStart >= 0) {
        const objectText = raw.slice(objectStart, i + 1);
        const title = readJsonStringField(objectText, 'title');
        const body = readJsonStringField(objectText, 'body');
        drafts.push({
          index: objectIndex,
          title: title.value ?? undefined,
          body: body.value ?? undefined,
        });
        objectStart = -1;
        objectIndex += 1;
      }
      continue;
    }

    if (ch === ']' && objectDepth === 0) break;
  }

  // Include currently-open object draft so title/body can render before object is complete.
  if (objectStart >= 0 && objectStart < raw.length) {
    const objectText = raw.slice(objectStart);
    const title = readJsonStringField(objectText, 'title');
    const body = readJsonStringField(objectText, 'body');
    if (title.value || body.value) {
      drafts.push({
        index: objectIndex,
        title: title.value ?? undefined,
        body: body.value ?? undefined,
      });
    }
  }

  return drafts;
}

function applyUpdateToPoints(
  points: UnderstandingPoint[],
  update: UnderstandingPointUpdate,
): UnderstandingPoint[] {
  if (!Number.isFinite(update.index) || update.index < 0) return points;
  const next = points.slice();
  while (next.length <= update.index) {
    next.push({ title: '', body: '' });
  }
  const current = next[update.index] || { title: '', body: '' };
  const nextTitle = update.title ?? current.title;
  const nextBody = update.bodyDelta != null
    ? `${current.body || ''}${update.bodyDelta}`
    : (update.body ?? current.body);
  next[update.index] = { title: String(nextTitle || ''), body: String(nextBody || '') };
  return next;
}

function parseSseEventData(data: string): any {
  const raw = data.trim();
  if (!raw || raw === '[DONE]') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

export async function streamUnderstandingConcepts(params: {
  locationName: string;
  languageCode: string;
  priorPoints?: UnderstandingPoint[];
  signal?: AbortSignal;
  onPoint?: (update: UnderstandingPointUpdate) => void;
}): Promise<UnderstandingPoint[]> {
  const { locationName, languageCode, priorPoints = [], signal, onPoint } = params;

  loginMachine.recordUsage('llm');

  const idToken = await loginMachine.getIdToken();

  const cleanPriorPoints = priorPoints
    .map((point) => ({
      title: String(point?.title || '').trim(),
      body: String(point?.body || '').trim(),
    }))
    .filter((point) => point.title && point.body)
    .slice(-12);

  const query = new URLSearchParams({
    location: locationName,
    languageCode,
  });

  if (idToken) query.set('authToken', idToken);

  const url = `${functionUrls.getLocationUnderstandingConcepts}?${query.toString()}`;
  const response = cleanPriorPoints.length
    ? await fetch(url, {
      method: 'POST',
      signal,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        location: locationName,
        languageCode,
        mode: 'more',
        priorPoints: cleanPriorPoints,
      }),
    })
    : await fetch(url, { method: 'GET', signal });
  if (!response.ok) {
    let errorCode = 'REQUEST_FAILED';
    try {
      const payload = await response.json();
      errorCode = String(payload?.error || payload?.message || errorCode);
    } catch {}
    throw new Error(errorCode);
  }

  if (!response.body) {
    const raw = await response.text();
    const parsed = tryParseJson(raw);
    const points = normalizePoints(parsed);
    if (!points.length) throw new Error('EMPTY_RESPONSE');
    return points;
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  const contentType = String(response.headers.get('content-type') || '').toLowerCase();
  let rawText = '';
  let sseBuffer = '';
  let points: UnderstandingPoint[] = [];
  let emitted = new Map<number, string>();
  let streamErrorCode: string | null = null;

  const emit = (update: UnderstandingPointUpdate) => {
    points = applyUpdateToPoints(points, update);
    const current = points[update.index] || { title: '', body: '' };
    const signature = `${current.title}\n${current.body}`;
    if (emitted.get(update.index) === signature) return;
    emitted.set(update.index, signature);
    if (process.env.NODE_ENV !== 'production') {
      const now = new Date().toISOString();
      console.info('[UnderstandStream]', now, `idx=${update.index}`, {
        title: current.title,
        bodyLen: current.body.length,
      });
    }
    onPoint?.(update);
  };

  const handleJsonPayload = (payload: any) => {
    if (!payload) return;
    if (typeof payload === 'object' && payload?.error) {
      streamErrorCode = String(payload.error || 'REQUEST_FAILED');
      return;
    }
    if (Array.isArray(payload?.points)) {
      payload.points.forEach((item: any, idx: number) => {
        emit({
          index: idx,
          title: String(item?.title || '').trim(),
          body: String(item?.body || '').trim(),
        });
      });
      return;
    }

    if (Array.isArray(payload)) {
      payload.forEach((item: any, idx: number) => {
        emit({
          index: idx,
          title: String(item?.title || '').trim(),
          body: String(item?.body || '').trim(),
        });
      });
      return;
    }

    if (payload && typeof payload === 'object') {
      const idx = Number.isFinite(payload.index) ? Number(payload.index) : 0;
      if (payload.bodyDelta != null) {
        emit({ index: idx, bodyDelta: String(payload.bodyDelta || '') });
      }
      if (payload.title != null || payload.body != null) {
        emit({
          index: idx,
          title: payload.title != null ? String(payload.title) : undefined,
          body: payload.body != null ? String(payload.body) : undefined,
        });
      }
    }
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    const chunk = decoder.decode(value, { stream: true });
    rawText += chunk;

    const mightBeSse = contentType.includes('text/event-stream') || chunk.includes('data:');
    if (mightBeSse) {
      sseBuffer += chunk;
      let boundary = sseBuffer.indexOf('\n\n');
      while (boundary >= 0) {
        const eventBlock = sseBuffer.slice(0, boundary);
        sseBuffer = sseBuffer.slice(boundary + 2);
        const dataLines = eventBlock
          .split('\n')
          .map((line) => line.trim())
          .filter((line) => line.startsWith('data:'))
          .map((line) => line.slice(5).trim());
        if (dataLines.length) {
          const eventData = dataLines.join('\n');
          const payload = parseSseEventData(eventData);
          if (payload !== '[DONE]') {
            handleJsonPayload(payload);
          }
        }
        boundary = sseBuffer.indexOf('\n\n');
      }
    }

    // Fallback parser for chunked JSON text streams (title/body extracted progressively).
    const drafts = extractObjectDraftsFromPointsArray(rawText);
    drafts.forEach((draft) => emit({
      index: draft.index,
      title: draft.title,
      body: draft.body,
    }));
  }

  rawText += decoder.decode();
  if (streamErrorCode) {
    throw new Error(streamErrorCode);
  }
  const finalParsed = tryParseJson(rawText);
  const finalPoints = normalizePoints(finalParsed);
  if (finalPoints.length) {
    points = finalPoints;
  } else {
    points = points
      .map((p) => ({ title: String(p.title || '').trim(), body: String(p.body || '').trim() }))
      .filter((p) => p.title && p.body);
  }

  if (!points.length) throw new Error('EMPTY_RESPONSE');
  return points;
}

export async function fetchUnderstandingConcepts(params: {
  locationName: string;
  languageCode: string;
  priorPoints?: UnderstandingPoint[];
  signal?: AbortSignal;
}): Promise<UnderstandingPoint[]> {
  return streamUnderstandingConcepts(params);
}
